import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePublicationsApi } from '../api/publications';
import type {
  PublicationAuthorOption,
  PublicationStatus,
  UpsertPublicationPayload,
} from '../features/publications/types';

type AdminPublicationFilters = {
  q: string;
  status: PublicationStatus | 'all';
  category?: string;
  subcategory?: string;
  page: number;
  enabled?: boolean;
};

export function usePublicPublications(enabled = true) {
  const api = usePublicationsApi();

  return useQuery({
    queryKey: ['public-publications'],
    queryFn: () => api.listPublications(),
    enabled,
    staleTime: 60_000,
  });
}

export function usePublicPublicationBySlug(slug: string | null, enabled = true) {
  const api = usePublicationsApi();

  return useQuery({
    queryKey: ['public-publication', slug],
    queryFn: () => (slug ? api.getPublicationBySlug(slug) : null),
    enabled: Boolean(slug) && enabled,
    staleTime: 60_000,
  });
}

export function useAdminPublications(filters: AdminPublicationFilters) {
  const api = usePublicationsApi();

  return useQuery({
    queryKey: ['admin-publications', filters],
    queryFn: () =>
      api.listAdminPublications({
        q: filters.q || undefined,
        status: filters.status,
        category: filters.category || undefined,
        subcategory: filters.subcategory || undefined,
        page: filters.page,
      }),
    enabled: filters.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useAdminPublication(publicationId: string | null) {
  const api = usePublicationsApi();

  return useQuery({
    queryKey: ['admin-publication', publicationId],
    queryFn: () => (publicationId ? api.getAdminPublication(publicationId) : null),
    enabled: Boolean(publicationId),
    staleTime: 30_000,
  });
}

export function usePublicationAuthors(enabled = true) {
  const api = usePublicationsApi();

  return useQuery<PublicationAuthorOption[]>({
    queryKey: ['publication-authors'],
    queryFn: () => api.listPublicationAuthors(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useCreatePublication() {
  const api = usePublicationsApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpsertPublicationPayload) => api.createPublication(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-publications'] });
      queryClient.invalidateQueries({ queryKey: ['admin-publication'] });
      queryClient.invalidateQueries({ queryKey: ['public-publications'] });
      queryClient.invalidateQueries({ queryKey: ['public-publication'] });
    },
  });
}

export function useUpdatePublication() {
  const api = usePublicationsApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ publicationId, payload }: { publicationId: string; payload: UpsertPublicationPayload }) =>
      api.updatePublication(publicationId, payload),
    onSuccess: (publication) => {
      queryClient.invalidateQueries({ queryKey: ['admin-publications'] });
      queryClient.invalidateQueries({ queryKey: ['admin-publication'] });
      queryClient.invalidateQueries({ queryKey: ['public-publications'] });
      queryClient.invalidateQueries({ queryKey: ['public-publication'] });
    },
  });
}

export function useDeletePublication() {
  const api = usePublicationsApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (publicationId: string) => api.deletePublication(publicationId),
    onSuccess: (_result, publicationId) => {
      queryClient.invalidateQueries({ queryKey: ['admin-publications'] });
      queryClient.removeQueries({ queryKey: ['admin-publication', publicationId] });
      queryClient.invalidateQueries({ queryKey: ['admin-publication'] });
      queryClient.invalidateQueries({ queryKey: ['public-publications'] });
      queryClient.invalidateQueries({ queryKey: ['public-publication'] });
    },
  });
}
